// Curated links published by FreeReading. This is a candidate library, NOT a
// replacement inventory or an assertion of perceptual/General American QA.
export const freeReadingSource = {
  publisher: 'FreeReading / Amplify Education, Inc.',
  publisherLocation: 'Brooklyn, New York, United States',
  license: 'CC-BY-SA-3.0',
  licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
  termsUrl: 'https://freereading.org/wiki/FreeReading%253AGeneral_disclaimer.html',
  soundKeyUrl: 'https://freereading.org/wiki/Most_common_sounds.html',
  lettersUrl: 'https://freereading.org/wiki/Most_common_letter_sounds_(audio_clips).html',
  combinationsUrl: 'https://freereading.org/wiki/Most_common_letter_combination_sounds_(audio_clips).html',
  speaker: 'Not identified by the publisher on the source pages',
  accentQa: 'Pending: US publisher is not proof of a consistent GA speaker system',
} as const

type SourceRow = [phoneme: string, file: string, spelling: string, example: string]
const letters: SourceRow[] = [
  ['short_a_ae', '2/26/Letter_a.mp3', 'a', 'hat'],
  ['b', 'c/c9/Letter_B.mp3', 'b', 'bat'],
  ['d', 'a/a8/Letter_D.mp3', 'd', 'dot'],
  ['short_e_eh', '3/37/Letter_e.mp3', 'e', 'bet'],
  ['f', 'a/a0/Letter_f.mp3', 'f', 'fat'],
  ['g', '9/9e/Letter_g.mp3', 'g', 'got'],
  ['h', 'c/cd/Letter_h.mp3', 'h', 'hot'],
  ['short_i_ih', 'b/be/Letter_i.mp3', 'i', 'sit'],
  ['j', '4/43/Letter_j.mp3', 'j', 'jet'],
  ['k', '7/7c/Letter_k.mp3', 'k', 'kit'],
  ['l', 'd/d9/Letter_l.mp3', 'l', 'let'],
  ['m', '2/29/Letter_m.mp3', 'm', 'mat'],
  ['n', 'c/c8/Letter_n.mp3', 'n', 'nut'],
  ['short_o_aa', '6/69/Letter_o.mp3', 'o', 'not'],
  ['p', '4/4d/Letter_p.mp3', 'p', 'pet'],
  ['r', 'a/a3/Letter_r.mp3', 'r', 'rat'],
  ['s', '1/18/Letter_s.mp3', 's', 'sat'],
  ['t', '8/88/Letter_t.mp3', 't', 'top'],
  ['short_u_uh', '7/75/Letter_u.mp3', 'u', 'cut'],
  ['v', 'c/c1/Letter_v.mp3', 'v', 'vet'],
  ['w', '9/9f/Letter_w.mp3', 'w', 'wet'],
  ['y_consonant', 'b/b1/Letter_y.mp3', 'y', 'yet'],
  ['z', '8/89/Letter_z.mp3', 'z', 'zoo'],
]
const combinations: SourceRow[] = [
  ['long_a', '2/2b/Ai_-_wait.mp3', 'ai', 'wait'],
  ['r_ar', '1/13/Ar_-_car.mp3', 'ar', 'car'],
  ['aw', '5/58/Aw_-_saw.mp3', 'aw', 'saw'],
  ['ch', 'b/bc/Ch_-_chip.mp3', 'ch', 'chip'],
  ['long_e', '2/21/Ee_-_tree.mp3', 'ee', 'tree'],
  ['r_er', 'f/fd/Er_-_fern.mp3', 'er', 'fern'],
  ['long_i', '0/09/Igh_-_high.mp3', 'igh', 'high'],
  ['long_o', '8/85/Oa_-_boat.mp3', 'oa', 'boat'],
  ['diphthong_oy', '1/16/Oi_-_boil.mp3', 'oi', 'boil'],
  ['long_u', '9/9b/Oo_-_moon.mp3', 'oo', 'moon'],
  ['r_or', 'a/a3/Or_-_fork.mp3', 'or', 'fork'],
  ['diphthong_ow', '3/31/Ou_-_cloud_take_2.mp3', 'ou', 'cloud'],
  ['sh', '4/43/Sh_-_ship.mp3', 'sh', 'ship'],
  ['th_unvoiced', 'b/bf/Th_-_thank.mp3', 'th', 'thank'],
]

export const audioReferenceCatalog = [
  ...letters.map((row) => ({ row, sourcePage: freeReadingSource.lettersUrl })),
  ...combinations.map((row) => ({ row, sourcePage: freeReadingSource.combinationsUrl })),
].map(({ row: [id, file, spelling, example], sourcePage }) => ({
  id: `freereading_${id}`, phonemeId: `phoneme_${id}`,
  sourceUrl: `https://freereading.org/w/images/${file}`,
  sourcePage, sourceSpelling: spelling, sourceExample: example,
}))

export const audioReferenceGaps: Record<string, string> = {
  phoneme_th_voiced: 'Published th example is thank /θ/, not this /ð/. Do not reuse it.',
  phoneme_ng: 'No isolated /ŋ/ entry on the checked sound pages. Do not substitute /n/ or /ŋg/.',
  phoneme_short_oo: 'Published oo example is moon /u/, not book /ʊ/.',
  phoneme_r_schwa: 'Stressed er/ir/ur clips do not establish an unstressed /ɚ/ target.',
  phoneme_r_air: 'No direct SQUARE /ɛɹ/ entry on the checked sound pages.',
  phoneme_r_ear: 'No direct NEAR /ɪɹ/ entry on the checked sound pages.',
  phoneme_schwa: 'No independent unstressed /ə/ entry. Do not substitute STRUT /ʌ/.',
}

export interface AudioReferenceRecord {
  id: string
  phonemeId: string
  ipa: string
  canonicalExample: string
  sourceUrl: string
  sourcePage: string
  sourceSpelling: string
  sourceExample: string
  sourcePath: string
  filePath: string
  sha256: string
  sourceSha256: string
  downloadedAt: string
  status: 'needs_review'
  method: string
  publisher?: string
  speaker?: string
  license?: string
  licenseUrl?: string
  sourceKind?: 'standalone_phoneme'
  sourceIpa?: string
  sourceFile?: string
  sourceRevision?: string
  nativeSpeakerEvidence?: string
  accentEvidence?: string
  signal: { durationMs: number; sampleRate: number; channels: number; warnings: string[] }
}
